import React, { useState, useEffect } from 'react';
import {
    Container,
    Header,
    Button,
    SpaceBetween,
    TextFilter,
    Box,
    Pagination,
    CollectionPreferences,
    Alert
} from '@amzn/awsui-components-console';
import { useAtomValue } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { ProfilesSectionProps } from '../../types';
import ProfilesTable from '../molecules/ProfilesTable';
import { useProfiles } from '../../hooks/useProfiles';

const ProfilesSection: React.FC<ProfilesSectionProps> = ({
    profiles: propsProfiles,
    loading: propsLoading,
    onCreateProfile,
    onProfileSelect
}) => {
    const [filteringText, setFilteringText] = useState('');
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isCreating, setIsCreating] = useState(false);
    const [sortingColumn, setSortingColumn] = useState<any>({
        sortingField: 'lastUpdated',
        sortingDescending: true
    });

    // Get authenticated user (username is already cleaned by getUserAtom)
    const authenticatedUser = useAtomValue(getUserAtom);
    const userId = authenticatedUser?.username || authenticatedUser?.attributes?.email;

    // Use API hook to fetch profiles for the current user
    const {
        profiles: apiProfiles,
        loading: apiLoading,
        error,
        hasMore,
        fetchNextPage,
        refresh
    } = useProfiles({
        userId: userId,
        autoFetch: true
    });

    // Combine props profiles with API profiles (props profiles take precedence for local updates)
    const allProfiles = propsProfiles.length > 0 ? [...propsProfiles, ...apiProfiles.filter(
        apiProfile => !propsProfiles.find(p => p.id === apiProfile.id)
    )] : apiProfiles;

    // Filter profiles based on search text (client-side filtering)
    const filteredProfiles = allProfiles.filter(profile =>
        profile.name.toLowerCase().includes(filteringText.toLowerCase()) ||
        profile.createdBy.toLowerCase().includes(filteringText.toLowerCase()) ||
        profile.description?.toLowerCase().includes(filteringText.toLowerCase())
    );

    // Sort filtered profiles
    const sortedProfiles = [...filteredProfiles].sort((a, b) => {
        if (!sortingColumn?.sortingField) return 0;

        let aValue: any;
        let bValue: any;

        switch (sortingColumn.sortingField) {
            case 'name':
                aValue = a.name;
                bValue = b.name;
                break;
            case 'createdBy':
                aValue = a.createdBy;
                bValue = b.createdBy;
                break;
            case 'lastUpdated':
                aValue = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
                bValue = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
                break;
            case 'ldapGroup':
                aValue = a.ldapGroup || '';
                bValue = b.ldapGroup || '';
                break;
            default:
                return 0;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        // Apply sort direction
        return sortingColumn.sortingDescending ? -comparison : comparison;
    });

    // Calculate pagination for client-side filtered results
    const totalItemsCount = sortedProfiles.length;
    const pagesCount = Math.ceil(totalItemsCount / pageSize);
    const startIndex = (currentPageIndex - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItemsCount);
    const paginatedProfiles = sortedProfiles.slice(startIndex, endIndex);

    // Determine loading state
    const loading = propsLoading || apiLoading || isCreating;

    // Refresh profiles when component mounts or after profile creation
    useEffect(() => {
        if (propsProfiles.length > apiProfiles.length && !apiLoading) {
            // A new profile was added locally, refresh from API
            refresh();
        }
    }, [propsProfiles.length]);


    const handleSortingChange = (sortingState: any) => {
        setSortingColumn(sortingState.sortingColumn);
    };

    const handleCreateClick = () => {
        onCreateProfile();
    };

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    counter={`(${totalItemsCount}${hasMore ? '+' : ''})`}
                    description="Profiles you and your team have access to"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                onClick={refresh}
                                iconName="refresh"
                                variant="normal"
                                loading={loading}
                            >
                            </Button>
                            <Button
                                variant="primary"
                                iconName="add-plus"
                                onClick={handleCreateClick}
                                loading={isCreating}
                                loadingText="Creating..."
                            >
                                Create
                            </Button>
                        </SpaceBetween>
                    }
                >
                    Your profiles
                </Header>
            }
        >
            <SpaceBetween size="l" direction="vertical">
                {error && (
                    <Alert
                        type="error"
                        dismissible
                        header="Error loading profiles"
                    >
                        {error}
                    </Alert>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <TextFilter
                            filteringText={filteringText}
                            filteringPlaceholder="Search profiles"
                            filteringAriaLabel="Filter profiles"
                            onChange={({ detail }) => {
                                setFilteringText(detail.filteringText);
                                setCurrentPageIndex(1); // Reset to first page on filter
                            }}
                            countText={`${sortedProfiles.length} matches`}
                        />
                    </div>

                    {totalItemsCount > 0 && (
                        <Pagination
                            currentPageIndex={currentPageIndex}
                            pagesCount={pagesCount}
                            onChange={({ detail }) =>
                                setCurrentPageIndex(detail.currentPageIndex)
                            }
                            ariaLabels={{
                                nextPageLabel: 'Next page',
                                previousPageLabel: 'Previous page',
                                pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                            }}
                        />
                    )}
                </div>

                <ProfilesTable
                    profiles={paginatedProfiles}
                    loading={loading}
                    onSelectionChange={() => {}}
                    onSortingChange={handleSortingChange}
                    onProfileSelect={onProfileSelect}
                />

                {hasMore && !filteringText && (
                    <Box textAlign="center">
                        <Button
                            onClick={fetchNextPage}
                            loading={apiLoading}
                            loadingText="Loading more..."
                            disabled={apiLoading}
                        >
                            Load more profiles
                        </Button>
                    </Box>
                )}
            </SpaceBetween>
            {/* Add spacing at bottom to prevent footer overlap */}
            <Box padding={{ bottom: "xxxl" }} />
        </Container>
    );
};

export default ProfilesSection;